import type { DeviceWithAuthDTO } from '@capture/client-api/src/orval'

import type { Action } from '../common/actions'
import { FilesDeletionSucceeded, FilesRestorationSucceeded } from '../job/actions'
import { FileUploadSucceeded } from '../uploader/actions'
import {
    ProfileNameChanged,
    ProfilePictureChangeFailed,
    ProfilePictureChangeStarted,
} from '../users/actions'

import type { RequireUserInfoReason, UserInformation } from './actions'
import {
    ConnectedDeviceWasDeleted,
    ConnectedDevicesWasFetched,
    DetectedThatUserIsUnauthenticated,
    FetchedAccountInfo,
    FetchedFileCount,
    HideMissingNameModal,
    InvalidLoginStateDetected,
    JobSubscriptionsDetected,
    MissingNameModalContext,
    ShowMissingNameModal,
    StartFetchingAccountInfo,
    TOSTermsVerified,
    UnableToFetchAccountInfo,
    UnverifiedTermsDetected,
    UserLoggedOut,
    UserSubscribedToAlbum,
    UserUnsubscribedFromAlbum,
    ValidUserInfoRequestDeniedByUser,
    ValidUserInfoRequired,
} from './actions'

export enum LoginProgressStep {
    NOT_STARTED = 'NOT_STARTED',
    FETCHING = 'FETCHING',
    AUTHENTICATED = 'AUTHENTICATED',
    FAILED = 'FAILED',
    UNAUTHENTICATED = 'UNAUTHENTICATED',
}

export type CurrentUserState = {
    loginStatus: LoginProgressStep
    haveFetchedAlbumList: boolean
    userInfoRequired: { isRequired: boolean; reason?: RequireUserInfoReason }
    accountInfo?: UserInformation
    isUploadingProfilePhoto: boolean
    subscribedJobs: JobID[]
    connectedDevices: DeviceWithAuthDTO[]
    missingNameModal: {
        isVisible: boolean
        context: MissingNameModalContext
    }
}

// Session middleware and loaders run before Redux store is initialized,
// so loginStatus will be updated via StartFetchingAccountInfo action when login begins.
const initialState: CurrentUserState = {
    loginStatus: LoginProgressStep.NOT_STARTED,
    haveFetchedAlbumList: false,
    userInfoRequired: { isRequired: false },
    subscribedJobs: [],
    connectedDevices: [],
    missingNameModal: {
        isVisible: false,
        context: MissingNameModalContext.NoContext,
    },
    isUploadingProfilePhoto: false,
}

export function currentUserReducer(
    state: CurrentUserState = initialState,
    action: Action,
): CurrentUserState {
    if (StartFetchingAccountInfo.match(action)) {
        return {
            ...state,
            loginStatus: LoginProgressStep.FETCHING,
        }
    }
    if (UnableToFetchAccountInfo.match(action)) {
        return { ...state, loginStatus: LoginProgressStep.FAILED }
    }
    if (FetchedAccountInfo.match(action)) {
        return {
            ...state,
            loginStatus: LoginProgressStep.AUTHENTICATED,
            accountInfo: action.payload,
            isUploadingProfilePhoto: false,
        }
    }
    if (DetectedThatUserIsUnauthenticated.match(action)) {
        return {
            ...state,
            loginStatus: LoginProgressStep.UNAUTHENTICATED,
            accountInfo: undefined,
        }
    }
    if (ProfileNameChanged.match(action)) {
        if (state.accountInfo) {
            return {
                ...state,
                accountInfo: {
                    ...state.accountInfo,
                    name: action.payload.name,
                },
            }
        }
    }
    if (ProfilePictureChangeStarted.match(action)) {
        return {
            ...state,
            isUploadingProfilePhoto: true,
        }
    }
    if (ProfilePictureChangeFailed.match(action)) {
        return {
            ...state,
            isUploadingProfilePhoto: false,
        }
    }
    if (UnverifiedTermsDetected.match(action) && state.accountInfo) {
        return {
            ...state,
            accountInfo: {
                ...state.accountInfo,
                tos_to_approve: action.payload,
            },
        }
    }
    if (TOSTermsVerified.match(action) && state.accountInfo) {
        return {
            ...state,
            accountInfo: { ...state.accountInfo, tos_to_approve: null },
        }
    }
    if (InvalidLoginStateDetected.match(action) || UserLoggedOut.match(action)) {
        return {
            ...state,
            loginStatus: LoginProgressStep.FAILED,
            accountInfo: undefined,
        }
    }
    if (ValidUserInfoRequired.match(action)) {
        return {
            ...state,
            userInfoRequired: { isRequired: true, reason: action.payload },
        }
    }
    if (ValidUserInfoRequestDeniedByUser.match(action)) {
        return { ...state, userInfoRequired: { isRequired: false } }
    }

    if (JobSubscriptionsDetected.match(action)) {
        return {
            ...state,
            subscribedJobs: action.payload,
            haveFetchedAlbumList: true,
        }
    }
    if (UserSubscribedToAlbum.match(action)) {
        return {
            ...state,
            subscribedJobs: state.subscribedJobs.concat([action.payload]),
        }
    }
    if (UserUnsubscribedFromAlbum.match(action)) {
        return {
            ...state,
            subscribedJobs: state.subscribedJobs.filter((id) => id !== action.payload),
        }
    }

    if (FileUploadSucceeded.match(action)) {
        let accInfo = state.accountInfo
        if (accInfo) {
            accInfo = { ...accInfo, used_space: action.payload.usedStorage }
        }
        return {
            ...state,
            accountInfo: accInfo,
        }
    }

    if (ConnectedDevicesWasFetched.match(action)) {
        const devices = action.payload.devices.filter(
            (d) => d.id !== action.payload.currentDeviceID,
        )
        return {
            ...state,
            connectedDevices: devices,
        }
    }
    if (ConnectedDeviceWasDeleted.match(action)) {
        const newState = { ...state }
        const devices = newState.connectedDevices.filter((d) => d.id !== action.payload.deviceID)
        return {
            ...newState,
            connectedDevices: devices,
        }
    }

    if (FilesDeletionSucceeded.match(action) || FilesRestorationSucceeded.match(action)) {
        if (state.accountInfo && action.payload.usedSpace) {
            return {
                ...state,
                accountInfo: {
                    ...state.accountInfo,
                    used_space: action.payload.usedSpace,
                },
            }
        }
    }

    if (FetchedFileCount.match(action)) {
        const accInfo = state.accountInfo
        if (accInfo) {
            return {
                ...state,
                accountInfo: {
                    ...accInfo,
                    ...action.payload,
                },
            }
        }
    }

    if (ShowMissingNameModal.match(action)) {
        return {
            ...state,
            missingNameModal: {
                isVisible: true,
                context: action.payload,
            },
        }
    }

    if (HideMissingNameModal.match(action)) {
        return {
            ...state,
            missingNameModal: {
                isVisible: false,
                context: MissingNameModalContext.NoContext,
            },
        }
    }

    return state
}

export const currentUserReducerMapObj = {
    currentUser: currentUserReducer,
}

export type StateWithCurrentUser = StateOfReducerMapObj<typeof currentUserReducerMapObj>
